import { useState } from 'react';
import { Button, Stack, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { auditApi } from 'App';

import {
  addAnomalyPayload,
  AnomalyAPI,
  AnomalyTypeAPI,
  getStartEndOffsets,
  Loader,
  Nullable,
} from '@shared';
import { Breadcrumb, Heading, SubHeading, Time } from './shared';
import * as S from '../AnomaliesSidebar.styled';
import { ManualAnomaliesPropsType } from '../AnomaliesSidebar.types';

export function ManualAnomalies(props: ManualAnomaliesPropsType) {
  const { anomalyTypes, playedSeconds, auditUuid, refetch, disabled = false, setPlaying } = props;

  const queryClient = useQueryClient();

  const [isShowAdding, setIsShowAdding] = useState(false);
  const [currentAnomaly, setCurrentAnomaly] = useState<AnomalyTypeAPI | null>(null);
  const [commentValue, setCommentValue] = useState('');

  const { mutate: addAnomaly, isLoading } = useMutation({
    mutationFn: (payload: addAnomalyPayload) => auditApi.addAnomaly(auditUuid, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(['anomalies', auditUuid], (oldData: Nullable<AnomalyAPI[]>) => {
        if (oldData) {
          return [data, ...oldData];
        } else {
          refetch();
          return [];
        }
      });
      setPlaying(true);
    },
  });

  const handleOpen = (anomaly: AnomalyTypeAPI) => {
    if (anomaly.editableComment) {
      setCurrentAnomaly(anomaly);
      setIsShowAdding(true);
      setPlaying(false);
    } else {
      addAnomaly({
        id: anomaly.id,
        ...getStartEndOffsets(anomaly.label, playedSeconds),
      });
    }
  };
  const handleClose = () => {
    setIsShowAdding(false);
  };
  const handleSendAnomaly = (anomaly: AnomalyTypeAPI) => {
    const newAnomaly = {
      id: anomaly.id,
      comment: commentValue,
      startOffset: playedSeconds,
    };
    addAnomaly(newAnomaly);
    setCommentValue('');
    handleClose();
  };
  if (isShowAdding && currentAnomaly) {
    return (
      <Stack gap={2}>
        <Breadcrumb backTo={'Manual Anomalies'} handleClose={handleClose} />
        <SubHeading title={currentAnomaly.label} />
        <Time fontSize={18} time={playedSeconds} />
        <TextField
          fullWidth
          label='Comments'
          multiline
          rows={4}
          value={commentValue}
          onChange={(e) => setCommentValue(e.target.value)}
        />
        <Button
          disabled={!commentValue}
          variant='contained'
          onClick={() => handleSendAnomaly(currentAnomaly)}
        >
          Add anomaly
        </Button>
      </Stack>
    );
  }

  return (
    <>
      <Heading title={'manual anomalies'} />
      <Stack gap={1}>
        {anomalyTypes?.map((anomaly) => (
          <S.AnomalyButton
            key={anomaly.id}
            variant='outlined'
            disabled={disabled}
            onClick={() => handleOpen(anomaly)}
          >
            {anomaly.label}
          </S.AnomalyButton>
        ))}
        {isLoading && <Loader />}
      </Stack>
    </>
  );
}
